import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_ALL_EXAM_PAPERS } from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";

interface FilterInputsProps {
  examPaperId: string;
  setExamPaperId: (value: string) => void;
  isArchived: string;
  setIsArchived: (value: string) => void;
  createdFrom: string;
  setCreatedFrom: (value: string) => void;
  createdTill: string;
  setCreatedTill: (value: string) => void;
  updatedFrom: string;
  setUpdatedFrom: (value: string) => void;
  updatedTill: string;
  setUpdatedTill: (value: string) => void;
  handleApplyFilter: () => void;
  handleClearFilter: () => void;
}

const FilterInputs: React.FC<FilterInputsProps> = ({
  examPaperId,
  setExamPaperId,
  isArchived,
  setIsArchived,
  createdFrom,
  setCreatedFrom,
  createdTill,
  setCreatedTill,
  updatedFrom,
  setUpdatedFrom,
  updatedTill,
  setUpdatedTill,
  handleApplyFilter,
  handleClearFilter,
}) => {
  const [examPaperSearchQuery, setExamPaperSearchQuery] = useState("");
  const [selectedExamPaper, setSelectedExamPaper] = useState<any>(null);

  const { data: examPapersData, loading: loadingExamPapers } = useQuery(
    GET_ALL_EXAM_PAPERS,
    {
      variables: {
        input: {
          limit: 10,
          offset: 0,
          title: examPaperSearchQuery || undefined,
        },
      },
      client: autoGradeClient,
      skip: false,
    }
  );

  const examPapers = examPapersData?.getExamPapers?.examPapers || [];
  useEffect(() => {
    if (examPaperId && examPapers.length > 0) {
      const foundPaper = examPapers.find((paper) => paper.id === examPaperId);
      setSelectedExamPaper(foundPaper || null);
    } else if (!examPaperId) {
      setSelectedExamPaper(null);
    }
  }, [examPaperId, examPapers]);
  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="h6" gutterBottom>
          Filter Answer Key
        </Typography>

        <Autocomplete
          fullWidth
          options={examPapers}
          getOptionLabel={(option) => option?.title || ""}
          value={selectedExamPaper}
          onChange={(event, newValue) => {
            setSelectedExamPaper(newValue);
            setExamPaperId(newValue ? newValue.id : "");
          }}
          onInputChange={(event, newInputValue) => {
            setExamPaperSearchQuery(newInputValue);
          }}
          loading={loadingExamPapers}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Exam Paper"
              variant="outlined"
              margin="normal"
              placeholder="Search exam papers..."
              helperText="Type to search by title"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body1">{option.title}</Typography>
                <Typography variant="caption" color="textSecondary">
                  ID: {option.id} | Created:{" "}
                  {new Date(option.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
          noOptionsText="No exam papers found"
          isOptionEqualToValue={(option, value) => option.id === value?.id}
        />

        <TextField
          select
          fullWidth
          label="Archived Status"
          variant="outlined"
          margin="normal"
          value={isArchived}
          onChange={(e) => setIsArchived(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>

        <TextField
          fullWidth
          label="Created From"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={createdFrom}
          onChange={(e) => setCreatedFrom(e.target.value)}
        />

        <TextField
          fullWidth
          label="Created Till"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={createdTill}
          onChange={(e) => setCreatedTill(e.target.value)}
        />

        <TextField
          fullWidth
          label="Updated From"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={updatedFrom}
          onChange={(e) => setUpdatedFrom(e.target.value)}
        />

        <TextField
          fullWidth
          label="Updated Till"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={updatedTill}
          onChange={(e) => setUpdatedTill(e.target.value)}
        />
      </Box>
      <Box
        position="sticky"
        bottom={0}
        p={2}
        borderTop="1px solid #ddd"
        bgcolor="white"
        zIndex={1}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleApplyFilter}
          sx={{ mb: 1 }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleClearFilter}
        >
          Clear Filters
        </Button>
      </Box>
    </Box>
  );
};

export default FilterInputs;
